<template>
  <div class="crud_form_wrapper">
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.add_stable") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Stable Avatar -->
          <base-image-upload-input
            col="12"
            identifier="avatar"
            :placeholder="$t('PLACEHOLDERS.stable_image')"
            @selectImage="selectStableImage"
            required
          />
          <!-- End:: Stable Avatar -->

          <!-- Start:: Additional Images -->
          <v-col cols="12">
            <UploadsPhotos
              :urls="imgUrls"
              multiple
              @onFileSelect="onFileSelect"
              @onFileRemove="onFileRemove"
            >
              {{ $t("PLACEHOLDERS.stable_images") }}
              <span class="text-danger">*</span>
            </UploadsPhotos>
          </v-col>
          <!-- End:: Additional Images -->

          <!-- Start:: Basic Information -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.stable_name')"
            v-model.trim="data.name"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model="data.mobile"
            required
          />

          <base-input
            col="6"
            type="email"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            required
          />

          <base-input
            col="6"
            type="password"
            :placeholder="$t('PLACEHOLDERS.password')"
            v-model.trim="data.password"
            required
          />

          <base-input
            col="6"
            type="password"
            :placeholder="$t('PLACEHOLDERS.confirmPassword')"
            v-model.trim="data.passwordConfirmation"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.commercial_registration_number')"
            v-model.trim="data.commercial_registration_number"
          />

          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.description')"
            v-model.trim="data.description"
          />
          <!-- End:: Basic Information -->

          <!-- Start:: Location Information -->
          <hr style="width: 98%" class="my-5" />
          <h5 class="font-weight-bold" style="color: #F7941E">
            {{ $t("PLACEHOLDERS.address") }}
          </h5>

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model.trim="data.city"
            required
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.district')"
            v-model.trim="data.district"
          />

          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.address')"
            v-model.trim="data.address"
          />

          <p class="mb-5 px-3" style="color: #F7941E; font-size: 16px">
            {{ $t("PLACEHOLDERS.location_on_map") }}
            <span class="text-danger"> * </span>
          </p>
          <ChangableMapComponent
            :lat="parseFloat(data.latitude)"
            :lng="parseFloat(data.longitude)"
            @update:lat="data.latitude = $event"
            @update:lng="data.longitude = $event"
            @update:placeName="data.location = $event"
          />
          <!-- End:: Location Information -->

          <div class="btn_wrapper">
            <base-button
              class="mt-5"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import UploadsPhotos from "@/components/formInputs/UploadsPhotos.vue";
import ChangableMapComponent from "@/components/general/ChangableMapComponent";

export default {
  name: "CreateStable",
  components: { UploadsPhotos, ChangableMapComponent },
  
  data() {
    return {
      isWaitingRequest: false,
      data: {
        avatar: { path: "", file: "" },
        name: "",
        mobile: "",
        email: "",
        password: "",
        passwordConfirmation: "",
        commercial_registration_number: "",
        description: "",
        latitude: 24.7136,
        longitude: 46.6753, // Riyadh coordinates as default
        city: "",
        district: "",
        address: "",
        location: "",
      },
      imgUrls: [],
      additionalImages: [],
    };
  },

  methods: {
    selectStableImage(selectedImage) {
      this.data.avatar = selectedImage;
    },

    onFileSelect(files) {
      this.additionalImages = files;
    },

    onFileRemove(index) {
      this.$delete(this.imgUrls, index);
      this.$delete(this.additionalImages, index);
    },

    validateFormInputs() {
      if (!this.data.name || !this.data.mobile || !this.data.email || !this.data.password) {
        this.$message.error(this.$t("MESSAGES.fillRequiredFields"));
        return;
      }

      if (this.data.password !== this.data.passwordConfirmation) {
        this.$message.error(this.$t("MESSAGES.passwordsNotMatch"));
        return;
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },

    async submitForm() {
      const formData = new FormData();

      // Append avatar
      if (this.data.avatar.file) {
        formData.append("avatar", this.data.avatar.file);
      }

      // Append additional images
      if (this.additionalImages && this.additionalImages.length > 0) {
        this.additionalImages.forEach((image, index) => {
          formData.append(`images[${index}]`, image);
        });
      }

      // Append basic data
      formData.append("name", this.data.name);
      formData.append("mobile", this.data.mobile);
      formData.append("email", this.data.email);
      formData.append("password", this.data.password);
      formData.append("password_confirmation", this.data.passwordConfirmation);
      
      if (this.data.commercial_registration_number) {
        formData.append("commercial_registration_number", this.data.commercial_registration_number);
      }
      
      if (this.data.description) {
        formData.append("description", this.data.description);
      }

      // Append location data
      formData.append("latitude", this.data.latitude);
      formData.append("longitude", this.data.longitude);
      formData.append("city", this.data.city);
      
      if (this.data.district) {
        formData.append("district", this.data.district);
      }
      
      if (this.data.address) {
        formData.append("address", this.data.address);
      }
      
      if (this.data.location) {
        formData.append("location", this.data.location);
      }

      try {
        await this.$axios.post("/stables", formData);
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push("/stables/all");
      } catch (error) {
        this.isWaitingRequest = false;
        if (error.response && error.response.data && error.response.data.message) {
          this.$message.error(error.response.data.message);
        } else {
          this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
        }
      }
    },
  },
};
</script>
